import React, { useState, useEffect, useMemo } from 'react';
import { Player } from '../types';
import { db, collection, addDoc, query, orderBy, where, getDocs, safeOnSnapshot, deleteDoc, doc, setDoc, updateDoc } from '../firebaseConfig';
import { isStaff, isStudent, isAdmin } from '../utils/roles';
import { checkAccessFor, AccessContext } from '../utils/access';
import { openInHalls } from '../utils/sso';

interface GuardianBoardProps {
  roomId: string;
  currentUser: Player;
  boardOwnerId: string;
  boardOwnerName: string;
  subjectName?: string;
  occupants?: Player[];
  classroomId?: string;
  classroomIds?: string[];
  accessCtx?: AccessContext;
  onClose: () => void;
}

const BUILT_IN_CATS = [
  { id: 'focus',    label: '💬 Dagens tanke',    color: 'border-blue-500/40',   bg: 'bg-blue-950/20' },
  { id: 'summary',  label: '📖 Vi arbejdede med', color: 'border-indigo-500/40', bg: 'bg-indigo-950/90', hasUrl: true },
  { id: 'homework', label: '✏️ Lektier',           color: 'border-red-500/40',    bg: 'bg-red-950/20',    hasUrl: true },
  { id: 'link',     label: '🔗 Link',              color: 'border-green-500/40',  bg: 'bg-green-950/20' },
  { id: 'general',  label: '📢 Generelt',          color: 'border-white/10',      bg: 'bg-white/5' },
];

const MOOD_LABELS: Record<number, { emoji: string; color: string; label: string }> = {
  1: { emoji: '😔', color: 'text-red-400',    label: 'Meget svært' },
  2: { emoji: '😕', color: 'text-orange-400', label: 'Svært' },
  3: { emoji: '😐', color: 'text-yellow-400', label: 'Okay' },
  4: { emoji: '🙂', color: 'text-green-400',  label: 'Godt' },
  5: { emoji: '🌟', color: 'text-indigo-400', label: 'Fantastisk' },
};

const s = (val: any): string => {
  if (typeof val === 'string') return val;
  if (!val) return '';
  if (typeof val === 'object') return val.da || val.en || Object.values(val).find((v: any) => typeof v === 'string') as string || '';
  return String(val);
};

const todayStr = () => new Date().toISOString().split('T')[0];

const HALLS_ORIGIN_PATH: Record<string, string> = {
  Astral: 'astral_mirrors',
  Atlas: 'science_atlas',
  Official: 'scroll_portal',
  Scriptorium: 'scriptorium',
  Halls: 'academy_halls',
};
// 17-8-2026: kilde-noegler fra Halls' INJECTION_SOURCES. Halls slaar selv
// jumpKey op, saa springet rammer det praecise item - ikke bare siden.
const HALLS_ORIGIN_SRC: Record<string, string> = {
  Astral: 'astral_missions',
  Atlas: 'atlas_missions',
  Official: 'official_missions',
  Scriptorium: 'scriptorium_docs',
};
const openPinnedActivity = (act: any) => {
  if (!act) return;
  const path = HALLS_ORIGIN_PATH[act.origin as string] || 'academy_halls';
  const srcKey = HALLS_ORIGIN_SRC[act.origin as string];
  // Atlas-missioner aabner paa nodeId (jumpIdField), ikke missionens eget id.
  const jumpId = (act.origin === 'Atlas' && act.nodeId) ? act.nodeId : act.id;
  if (srcKey) openInHalls(path, jumpId, srcKey);
  else openInHalls(path, act.id);
};

// ─── TYPER ───────────────────────────────────────────────────────────────────

interface JournalEntry {
  date: string;
  mood: number;
  good: string;
  hard: string;
  remember: string;
  subjects: { subjectName: string; notes: string; links: { url: string; title: string }[] }[];
  visitStart: number;
  visitEnd?: number;
  roomName: string;
  guardianFeedback?: string;
  guardianFeedbackAt?: number;
}

type JournalFilter = 'all' | 'today' | 'missing' | 'low_mood' | 'no_feedback';
type JournalSort = 'name' | 'mood_asc';

// ─── STUDENT JOURNAL CARD ────────────────────────────────────────────────────

interface StudentJournalCardProps {
  student: Player;
  isOpen: boolean;
  onToggle: () => void;
}

const StudentJournalCard: React.FC<StudentJournalCardProps> = ({ student, isOpen, onToggle }) => {
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [feedbackInput, setFeedbackInput] = useState<Record<string, string>>({});
  const [savingFeedback, setSavingFeedback] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDocs(
          query(collection(db, `daily_reflections/${student.id}/entries`), orderBy('visitStart', 'desc'))
        );
        setEntries(snap.docs.map(d => ({ ...d.data(), date: d.id } as JournalEntry)));
      } catch { /* ingen entries */ }
      finally { setLoading(false); }
    };
    load();
  }, [student.id]);

  const todayEntry = entries.find(e => e.date === todayStr());
  const recentEntries = entries.slice(0, 5);
  const wizardName = s(student.wizardName || (student as any).username || student.name);
  const realName = s(student.realName || '');

  const handleSaveFeedback = async (date: string) => {
    const text = feedbackInput[date]?.trim();
    if (!text) return;
    setSavingFeedback(prev => ({ ...prev, [date]: true }));
    try {
      await updateDoc(doc(db, `daily_reflections/${student.id}/entries`, date), {
        guardianFeedback: text,
        guardianFeedbackAt: Date.now(),
      });
      setEntries(prev => prev.map(e =>
        e.date === date ? { ...e, guardianFeedback: text, guardianFeedbackAt: Date.now() } : e
      ));
      setFeedbackInput(prev => ({ ...prev, [date]: '' }));
    } catch (e) { console.error(e); }
    finally { setSavingFeedback(prev => ({ ...prev, [date]: false })); }
  };

  // Statusdot til kompakt visning
  const statusDot = () => {
    if (loading) return <span className="w-2 h-2 rounded-full bg-white/20 shrink-0" />;
    if (!todayEntry) return <span className="w-2 h-2 rounded-full bg-red-500/60 shrink-0" title="Ingen entry i dag" />;
    if (todayEntry.mood <= 2) return <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse shrink-0" title="Lavt humør" />;
    if (!todayEntry.guardianFeedback) return <span className="w-2 h-2 rounded-full bg-yellow-400/60 shrink-0" title="Afventer feedback" />;
    return <span className="w-2 h-2 rounded-full bg-green-500/60 shrink-0" title="Feedback givet" />;
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-all text-left"
      >
        {statusDot()}
        <img
          src={student.avatarUrl || '/default-avatar.png'}
          alt=""
          className="w-7 h-7 rounded-full object-cover border border-white/20 shrink-0"
        />
        <div className="flex-1 min-w-0">
          <p className="text-white text-xs font-bold uppercase tracking-wide truncate">{wizardName}</p>
          {realName && realName !== wizardName && (
            <p className="text-white/35 text-[9px] truncate">{realName}</p>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {loading ? (
            <span className="text-white/20 text-[9px]">...</span>
          ) : todayEntry ? (
            <div className="flex items-center gap-1.5">
              <span className="text-base">{MOOD_LABELS[todayEntry.mood]?.emoji || '📝'}</span>
              {!todayEntry.guardianFeedback && (
                <span className="text-[7px] font-black uppercase text-yellow-400/70 bg-yellow-500/10 px-1.5 py-0.5 rounded">
                  Afventer
                </span>
              )}
            </div>
          ) : (
            <span className="text-[10px] font-black uppercase text-red-400/60">Mangler</span>
          )}
          <span className="text-white/60 text-[10px]">{isOpen ? '▲' : '▼'}</span>
        </div>
      </button>

      {isOpen && (
        <div className="border-t border-white/10 p-4 space-y-3">
          {loading && <p className="text-white/60 text-[10px] italic text-center py-4">Henter dagbog...</p>}
          {!loading && entries.length === 0 && (
            <p className="text-white/20 text-[10px] italic text-center py-4">Ingen dagbogsindlæg endnu</p>
          )}

          {!loading && recentEntries.map(entry => {
            const isToday = entry.date === todayStr();
            const dateLabel = isToday
              ? 'I dag'
              : new Date(entry.date).toLocaleDateString('da-DK', { weekday: 'short', day: 'numeric', month: 'short' });

            return (
              <div key={entry.date}
                className={`rounded-xl border p-3 space-y-2 ${isToday ? 'border-indigo-500/30 bg-indigo-950/20' : 'border-white/5 bg-black/20'}`}>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {entry.mood > 0 && (
                      <span className={`text-sm ${MOOD_LABELS[entry.mood]?.color}`}>
                        {MOOD_LABELS[entry.mood]?.emoji}
                      </span>
                    )}
                    <span className={`text-[10px] font-black uppercase ${isToday ? 'text-indigo-300' : 'text-white/70'}`}>
                      {dateLabel}
                    </span>
                    {entry.roomName && <span className="text-xs text-white/20">{entry.roomName}</span>}
                  </div>
                  {entry.visitStart && entry.visitEnd && (
                    <span className="text-xs text-white/20 font-mono">
                      {Math.round((entry.visitEnd - entry.visitStart) / 60000)} min
                    </span>
                  )}
                </div>

                {entry.good && (
                  <div>
                    <p className="text-[10px] font-black uppercase text-green-400/70 mb-0.5">🌟 Gik godt</p>
                    <p className="text-white/70 text-[11px] leading-relaxed">{entry.good}</p>
                  </div>
                )}
                {entry.hard && (
                  <div>
                    <p className="text-[10px] font-black uppercase text-orange-400/70 mb-0.5">🧩 Var svært</p>
                    <p className="text-white/70 text-[11px] leading-relaxed">{entry.hard}</p>
                  </div>
                )}
                {entry.remember && (
                  <div>
                    <p className="text-[10px] font-black uppercase text-indigo-400/70 mb-0.5">📌 Husk</p>
                    <p className="text-white/70 text-[11px] leading-relaxed">{entry.remember}</p>
                  </div>
                )}

                {entry.subjects?.length > 0 && (
                  <div className="pt-1 space-y-1 border-t border-white/5">
                    {entry.subjects.map((subj, i) => (
                      <div key={i}>
                        <p className="text-[10px] font-black uppercase text-yellow-400/70">📚 {subj.subjectName}</p>
                        {subj.notes && <p className="text-white/60 text-[11px] leading-relaxed">{subj.notes}</p>}
                        {subj.links?.map((l, j) => (
                          <a key={j} href={l.url} target="_blank" rel="noopener noreferrer"
                            className="text-[9px] text-indigo-400 hover:text-white underline block">
                            🔗 {l.title}
                          </a>
                        ))}
                      </div>
                    ))}
                  </div>
                )}

                {/* Eksisterende feedback */}
                {entry.guardianFeedback && (
                  <div className="bg-indigo-950/40 border border-indigo-500/20 rounded-lg p-2 mt-1">
                    <p className="text-[10px] font-black uppercase text-indigo-400 mb-0.5">🖋️ Vogterens svar</p>
                    <p className="text-white/70 text-[11px] leading-relaxed">{entry.guardianFeedback}</p>
                  </div>
                )}

                {/* Feedback formular */}
                <div className="pt-2 border-t border-white/5 space-y-1.5">
                  <p className="text-[10px] font-black uppercase text-white/25">
                    {entry.guardianFeedback ? 'Ret feedback' : 'Skriv feedback'}
                  </p>
                  <textarea
                    value={feedbackInput[entry.date] ?? entry.guardianFeedback ?? ''}
                    onChange={e => setFeedbackInput(prev => ({ ...prev, [entry.date]: e.target.value }))}
                    placeholder="Skriv en kort kommentar til eleven..."
                    rows={2}
                    className="w-full bg-black/30 border border-white/10 rounded-lg px-2.5 py-2 text-white/80 text-[10px] outline-none focus:border-indigo-500/40 resize-none"
                  />
                  <button
                    onClick={() => handleSaveFeedback(entry.date)}
                    disabled={savingFeedback[entry.date] || !feedbackInput[entry.date]?.trim()}
                    className="w-full py-1.5 bg-indigo-600/40 hover:bg-indigo-600 border border-indigo-500/30 rounded-lg text-[10px] font-black uppercase text-indigo-300 hover:text-white transition-all disabled:opacity-30"
                  >
                    {savingFeedback[entry.date] ? 'Gemmer...' : entry.guardianFeedback ? 'Opdater ✨' : 'Besegl feedback ✨'}
                  </button>
                </div>
              </div>
            );
          })}

          {entries.length > 5 && (
            <p className="text-[11px] text-white/20 italic text-center">+ {entries.length - 5} ældre indlæg</p>
          )}
        </div>
      )}
    </div>
  );
};

// ─── HOVED KOMPONENT ──────────────────────────────────────────────────────────

const GuardianBoard: React.FC<GuardianBoardProps> = ({
  roomId, currentUser, boardOwnerId, boardOwnerName, subjectName, occupants, classroomId, classroomIds, accessCtx, onClose
}) => {
  const [messages, setMessages] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'board' | 'journals' | 'archive'>('board');
  const [archiveWeek, setArchiveWeek] = useState<string | null>(null);
  const [archiveSearchDate, setArchiveSearchDate] = useState("");
  const [archiveSearchGuardian, setArchiveSearchGuardian] = useState("");
  const [draftSections, setDraftSections] = useState<Record<string, string>>({});
  const [draftLinks, setDraftLinks] = useState<Record<string, string>>({});

  const [activeDraftCats, setActiveDraftCats] = useState<string[]>(['general']);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingSections, setEditingSections] = useState<Record<string, any>>({});
  const [draftSectionVisibility, setDraftSectionVisibility] = useState<Record<string, string>>({});
  const [showAssign, setShowAssign] = useState(false);
  const [boardTitle, setBoardTitle] = useState(subjectName || '');
  const [editingTitle, setEditingTitle] = useState(false);
  const [students, setStudents] = useState<Player[]>([]);
  const [draftSectionStudents, setDraftSectionStudents] = useState<Record<string, string[]>>({});

  // Journal states
  const [openStudentId, setOpenStudentId] = useState<string | null>(null);
  const [journalFilter, setJournalFilter] = useState<JournalFilter>('all');
  const [journalSort, setJournalSort] = useState<JournalSort>('name');
  const [studentEntries, setStudentEntries] = useState<Record<string, JournalEntry[]>>({});
  const [loadingEntries, setLoadingEntries] = useState(false);

  const isOwner = currentUser.id === boardOwnerId;
  const canManage = isOwner || isAdmin(currentUser.role);
  const isGuardian = isStaff(currentUser.role);
  // 18-8-2026: kildevaelger. Kataloget hentes fra Halls (injectionCatalogForGuardian),
  // saa registeret bor ET sted og Campus ikke faar en kopi der driver fra hinanden.
  const [srcCatalog, setSrcCatalog] = useState<any[]>([]);
  const [draftSrc, setDraftSrc] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!isGuardian) return;
    let afbrudt = false;
    (async () => {
      try {
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        const fn = httpsCallable<{}, { sources: any[] }>(getFunctions(undefined, 'europe-west3'), 'injectionCatalogForGuardian');
        const { data } = await fn({});
        if (!afbrudt) setSrcCatalog(((data as any) && (data as any).sources) || []);
      } catch (e) { if (!afbrudt) setSrcCatalog([]); }
    })();
    return () => { afbrudt = true; };
  }, [isGuardian]);
  const HALLS_URL = 'https://halls.norewyn.academy/';
  const byggKildeLink = (srcKey: string, itemId: string): string => {
    if (!srcKey || !itemId) return '';
    const k = srcCatalog.find((x: any) => x.key === srcKey);
    // 30-8-2026: fagets egne opgaver er IKKE en katalog-kilde. De kommer fra
    // subjectActivities (samme liste pin-vaelgeren bruger) og springes med den
    // simple ?id=-form, som Halls' bro laeser: App.tsx:131 laegger id'et i
    // dra_open_activity for academy_halls.
    if (srcKey === '_subject_activities') {
      return HALLS_URL + '#/academy_halls?id=' + encodeURIComponent(itemId);
    }
    const route = (k && k.route) || 'dashboard';
    return HALLS_URL + '#/' + route + '?src=' + encodeURIComponent(srcKey) + '&id=' + encodeURIComponent(itemId);
  };
  const isCurrentUserStudent = isStudent(currentUser.role);
  const [linkedSubjectId, setLinkedSubjectId] = useState<string>('');
  const [pinnedActivity, setPinnedActivity] = useState<any>(null);
  const [subjectDailyMsg, setSubjectDailyMsg] = useState<string>('');
  const [subjectDailyAt, setSubjectDailyAt] = useState<number>(0);
  // campus26 (13/9): samme levetider som Borgpladsen (Halls borg25). Uden stempel = gammel.
  const _beskedGammel = (msg: string, at: number): boolean => !!msg && (!at || at < Date.now() - 14 * 86400000);
  const _fokusUdloebet = (act: any): boolean => !!(act && act.deadline && Date.now() > Number(act.deadline) + 3 * 86400000);
  const [subjectActivities, setSubjectActivities] = useState<any[]>([]);
  const [pinnedActivityId, setPinnedActivityId] = useState<string>('');
  const [subjectResourceLinks, setSubjectResourceLinks] = useState<{title:string;url:string}[]>([]);
  const [linkedHallId, setLinkedHallId] = useState<string>('');
  const [hallsData, setHallsData] = useState<any[]>([]);

  useEffect(() => {
    const loadBoard = async () => {
      const { getDoc, doc: d2 } = await import('firebase/firestore');
      const snap = await getDoc(d2(db, `room_notices/${roomId}/boards/${boardOwnerId}`));
      if (snap.exists()) {
        const data = snap.data();
        if (data.linkedSubjectId) setLinkedSubjectId(data.linkedSubjectId);
        if (data.linkedHallId) setLinkedHallId(data.linkedHallId);
      }
    };
    const fetchHalls = async () => {
      // Phase 3: hent classrooms + flat subjects i parallel
      const { getDocs, collection: col } = await import('firebase/firestore');
      const [classSnap, subjSnap] = await Promise.all([
        getDocs(col(db, 'classrooms')),
        getDocs(col(db, 'subjects')),
      ]);
      const allSubj = subjSnap.docs.map(d => ({ id: d.id, ...(d.data() as any) }));
      setHallsData(classSnap.docs.map(d => {
        const cData = d.data() as any;
        const flatForClass = allSubj.filter((s: any) => s.classroomId === d.id);
        const subjects = flatForClass.length > 0 ? flatForClass : (cData.subjects || []);
        return { id: d.id, name: cData.name || '', subjects };
      }));
    };
    if (isOwner || isGuardian) { loadBoard(); fetchHalls(); }
  }, [roomId, boardOwnerId, isOwner, isGuardian]);

  // Hent pinned activity + dailyMessage fra koblet fagdør
  useEffect(() => {
    if (!linkedSubjectId || !linkedHallId) { setPinnedActivity(null); setSubjectDailyMsg(''); setSubjectDailyAt(0); return; }
    const fetchLinked = async () => {
      // Phase 3: laes subject direkte fra flat collection; topics+activities via FK
      const { getDoc, getDocs, collection: col, query: q, where: w, doc: d2 } = await import('firebase/firestore');
      let subj: any = null;
      try {
        const subjDoc = await getDoc(d2(db, 'subjects', linkedSubjectId));
        if (subjDoc.exists()) subj = { id: subjDoc.id, ...(subjDoc.data() as any) };
      } catch (e) { /* fallback */ }
      if (!subj) {
        // Fallback til gammel nested struktur
        const hallSnap = await getDoc(d2(db, 'classrooms', linkedHallId));
        if (!hallSnap.exists()) return;
        const subjects = hallSnap.data().subjects || [];
        subj = subjects.find((s: any) => s.id === linkedSubjectId);
        if (!subj) return;
      }
      setSubjectDailyMsg(subj.dailyMessage?.da || '');
      setSubjectDailyAt(Number((subj as any).dailyMessageAt) || 0);
      // Hent topics+activities via flat collections hvis de eksisterer, ellers brug nested
      let topicsForSubj: any[] = subj.topics || [];
      try {
        const tSnap = await getDocs(q(col(db, 'topics'), w('subjectId', '==', linkedSubjectId)));
        if (!tSnap.empty) {
          const aSnap = await getDocs(q(col(db, 'activities'), w('subjectId', '==', linkedSubjectId)));
          const allActsForSubj = aSnap.docs.map(d => ({ id: d.id, ...(d.data() as any) }));
          topicsForSubj = tSnap.docs.map(d => {
            const tData = d.data() as any;
            return { ...tData, id: d.id, activities: allActsForSubj.filter(a => a.topicId === d.id) };
          });
        }
      } catch (e) { /* fallback til nested */ }
      const allActs = topicsForSubj.flatMap((t: any) =>
        (t.activities || []).filter((a: any) => !a.isArchived).map((a: any) => ({
          ...a, topicTitle: typeof t.title === 'string' ? t.title : t.title?.da || t.title?.en || ''
        }))
      );
      // Hent eksterne missions koblet til fagdørens topics
      const topicIds = (subj.topics || []).map((t: any) => t.id);
      // getDocs, col, q, w er allerede i scope fra tidligere dynamic import
      const externalActs: any[] = [];
      if (topicIds.length > 0) {
        // Astral
        try {
          const astralSnap = await getDocs(q(col(db, 'astral_missions'), w('published', '==', true)));
          astralSnap.forEach((d: any) => {
            const data = { id: d.id, ...d.data() };
            if (topicIds.includes(data.targetTopicId))
              externalActs.push({ ...data, isExternal: true, origin: 'Astral', topicTitle: 'Astralspejlet' });
          });
        } catch(e) {}
        // Atlas
        try {
          const atlasSnap = await getDocs(q(col(db, 'atlas_missions'), w('published', '==', true)));
          atlasSnap.forEach((d: any) => {
            const data = { id: d.id, ...d.data() };
            if (topicIds.includes(data.targetTopicId))
              externalActs.push({ ...data, isExternal: true, origin: 'Atlas', topicTitle: 'Verdenskortet' });
          });
        } catch(e) {}
        // Official (GuardianForge/ScrollPortal)
        try {
          const officialSnap = await getDocs(q(col(db, 'official_missions'), w('published', '==', true)));
          officialSnap.forEach((d: any) => {
            const data = { id: d.id, ...d.data() };
            if (topicIds.includes(data.targetTopicId))
              externalActs.push({ ...data, isExternal: true, origin: 'Official', topicTitle: 'Officielt Portal' });
          });
        } catch(e) {}
        // Scriptorium (forberedt — henter docs med targetTopicId når det er bygget)
        try {
          const scriptSnap = await getDocs(q(col(db, 'scriptorium_docs'), w('published', '==', true)));
          scriptSnap.forEach((d: any) => {
            const data = { id: d.id, ...d.data() };
            if (topicIds.includes(data.targetTopicId))
              externalActs.push({ ...data, isExternal: true, origin: 'Scriptorium', topicTitle: 'Scriptorium' });
          });
        } catch(e) {}
      }
      const combined = [...allActs, ...externalActs];
      setSubjectActivities(combined);
      setPinnedActivityId(subj.pinnedActivityId || '');
      setSubjectResourceLinks(subj.resourceLinks || []);
      if (subj.pinnedActivityId) {
        const pinned = combined.find((a: any) => a.id === subj.pinnedActivityId);
        setPinnedActivity(pinned || null);
      } else { setPinnedActivity(null); }
    };
    fetchLinked();
  }, [linkedSubjectId, linkedHallId]);

  const saveBoardTitle = async () => {
    await setDoc(doc(db, `room_notices/${roomId}/boards/${boardOwnerId}`), {
      guardianName: boardOwnerName, subjectName: boardTitle, subjectTitle: boardTitle, lastUpdated: Date.now(),
      ...(linkedSubjectId ? { linkedSubjectId, linkedHallId } : {}),
    }, { merge: true });
    setEditingTitle(false);
  };

  useEffect(() => {
    const q = query(
      collection(db, `room_notices/${roomId}/boards/${boardOwnerId}/messages`),
      orderBy('timestamp', 'desc')
    );
    const unsub = safeOnSnapshot(q, (snap: any) => {
      const msgs: any[] = [];
      snap.forEach((d: any) => msgs.push({ ...d.data(), id: d.id }));
      setMessages(msgs);
      localStorage.setItem(`board_seen_${roomId}_${boardOwnerId}`, Date.now().toString());
    });
    return () => unsub();
  }, [roomId, boardOwnerId]);

  useEffect(() => {
    // Multi-classroom: foretraek classroomIds, fallback til legacy classroomId
    const effClassroomIds: string[] = (classroomIds && classroomIds.length > 0)
      ? classroomIds
      : (classroomId ? [classroomId] : []);
    let cancelled = false;
    const mapAndFilter = (snap: any): Player[] => {
      const loaded: Player[] = [];
      snap.forEach((d: any) => {
        const data = d.data() as any;
        loaded.push({ ...data, id: d.id, wizardName: data.username || data.wizardName || '', realName: data.realName || '' } as Player);
      });
      let filtered = loaded.filter(u => isStudent(u.role));
      // Filtrer gennem checkAccessFor saa kun elever med faktisk room-adgang vises
      if (accessCtx) {
        filtered = filtered.filter(u => checkAccessFor(u, roomId, accessCtx).hasAccess);
      }
      return filtered;
    };
    (async () => {
      let result: Player[] = [];
      if (effClassroomIds.length > 0) {
        // Firestore array-contains-any har max 30 vaerdier
        const snap = await getDocs(query(collection(db, 'users'), where('classIds', 'array-contains-any', effClassroomIds.slice(0, 30))));
        result = mapAndFilter(snap);
      }
      // FALLBACK: intet classroom koblet ELLER koblingen gav 0 elever ->
      // hent alle elever og filtrer paa faktisk room-adgang (checkAccessFor)
      if (result.length === 0 && accessCtx && isGuardian) {
        const snap = await getDocs(query(collection(db, 'users'), where('role', '==', 'student')));
        result = mapAndFilter(snap);
      }
      if (!cancelled) setStudents(result);
    })().catch(() => { if (!cancelled) setStudents([]); });
    return () => { cancelled = true; };
  }, [classroomId, classroomIds, accessCtx, roomId]);

  // Kolleger (vogtere) for rummets classroom — kilde til "Tildel til kollega"
  const [colleagues, setColleagues] = useState<Player[]>([]);
  useEffect(() => {
    const effIds: string[] = (classroomIds && classroomIds.length > 0)
      ? classroomIds
      : (classroomId ? [classroomId] : []);
    if (effIds.length === 0) { setColleagues([]); return; }
    let cancelled = false;
    (async () => {
      const { getDoc } = await import('firebase/firestore');
      const found: Player[] = [];
      for (const cId of effIds.slice(0, 30)) {
        const clsDoc = await getDoc(doc(db, 'classrooms', cId));
        if (!clsDoc.exists()) continue;
        const clsData = clsDoc.data() as any;
        const gIds: string[] = clsData.guardianIds || (clsData.primaryGuardianId ? [clsData.primaryGuardianId] : []);
        const subjGIds: string[] = [];
        try {
          const subjSnap = await getDocs(query(collection(db, 'subjects'), where('classroomId', '==', cId)));
          subjSnap.docs.forEach(d => {
            const s = d.data() as any;
            if (s.primaryGuardianId) subjGIds.push(s.primaryGuardianId);
            (s.assistantTeacherIds || []).forEach((id: string) => subjGIds.push(id));
          });
        } catch {
          (clsData.subjects || []).forEach((s: any) => {
            if (s.primaryGuardianId) subjGIds.push(s.primaryGuardianId);
            (s.assistantTeacherIds || []).forEach((id: string) => subjGIds.push(id));
          });
        }
        for (const gid of [...new Set([...gIds, ...subjGIds])]) {
          if (found.find(m => m.id === gid)) continue;
          const gDoc = await getDoc(doc(db, 'users', gid));
          if (!gDoc.exists()) continue;
          const data = gDoc.data() as any;
          if (data.role === 'guardian') {
            found.push({ id: gDoc.id, ...data, wizardName: data.username || data.wizardName || '', realName: data.realName || '' } as Player);
          }
        }
      }
      if (!cancelled) setColleagues(found);
    })();
    return () => { cancelled = true; };
  }, [classroomId, (classroomIds || []).join('|')]);

  // Hent alle elevers entries når journals-tab åbnes.
  // 18-8-2026: LOEKKE-FIX. Dependency var [students] - et NYT array ved hver
  // setStudents, saa effekten kaldte sig selv i ring og fanen blinkede.
  // Nu afhaenger den af elev-ID'erne som en stabil streng, og en afbryd-flag
  // sikrer at et forladt kald ikke skriver state efter unmount/tab-skift.
  const studentIdsKey = useMemo(() => students.map(s2 => s2.id).sort().join(','), [students]);
  useEffect(() => {
    if (activeTab !== 'journals' || !studentIdsKey) return;
    let afbrudt = false;
    setLoadingEntries(true);
    const ids = studentIdsKey.split(',').filter(Boolean);
    const fetchAll = async () => {
      const result: Record<string, JournalEntry[]> = {};
      await Promise.all(ids.map(async (sid: string) => {
        try {
          const snap = await getDocs(query(collection(db, `daily_reflections/${sid}/entries`), orderBy('visitStart', 'desc')));
          result[sid] = snap.docs.map(d => ({ ...d.data(), date: d.id } as JournalEntry));
        } catch { result[sid] = []; }
      }));
      if (afbrudt) return;
      setStudentEntries(result);
      setLoadingEntries(false);
    };
    fetchAll();
    return () => { afbrudt = true; };
  }, [activeTab, studentIdsKey]);

  // Journal statistik
  const journalStats = useMemo(() => {
    const today = todayStr();
    const total = students.length;
    const hasEntry = students.filter(st => studentEntries[st.id]?.some(e => e.date === today)).length;
    const lowMood = students.filter(st => { const e = studentEntries[st.id]?.find(e => e.date === today); return e && e.mood <= 2; }).length;
    const noFeedback = students.filter(st => { const e = studentEntries[st.id]?.find(e => e.date === today); return e && !e.guardianFeedback; }).length;
    return { total, hasEntry, lowMood, noFeedback };
  }, [students, studentEntries]);

  // Filtrér og sortér
  const filteredStudents = useMemo(() => {
    const today = todayStr();
    let list = [...students];
    switch (journalFilter) {
      case 'today':       list = list.filter(st => studentEntries[st.id]?.some(e => e.date === today)); break;
      case 'missing':     list = list.filter(st => !studentEntries[st.id]?.some(e => e.date === today)); break;
      case 'low_mood':    list = list.filter(st => { const e = studentEntries[st.id]?.find(e => e.date === today); return e && e.mood <= 2; }); break;
      case 'no_feedback': list = list.filter(st => { const e = studentEntries[st.id]?.find(e => e.date === today); return e && !e.guardianFeedback; }); break;
    }
    if (journalSort === 'mood_asc') {
      list.sort((a, b) => {
        const today = todayStr();
        const mA = studentEntries[a.id]?.find(e => e.date === today)?.mood ?? 99;
        const mB = studentEntries[b.id]?.find(e => e.date === today)?.mood ?? 99;
        return mA - mB;
      });
    } else {
      list.sort((a, b) => s(a.wizardName || (a as any).username || a.name).localeCompare(s(b.wizardName || (b as any).username || b.name)));
    }
    return list;
  }, [students, studentEntries, journalFilter, journalSort]);

  const toggleDraftCat = (catId: string) => {
    setActiveDraftCats(prev => prev.includes(catId) ? prev.filter(c => c !== catId) : [...prev, catId]);
  };

  const handlePost = async () => {
    const hasContent = activeDraftCats.some(cat => draftSections[cat]?.trim());
    if (!hasContent) return;
    // campus25 (13/9): rulle-opslag roerer IKKE fagdoerens dagens besked. Den har sit eget felt med
    // dato-stempel (dailyMessageAt) laengere nede — overskrivningen her satte gamle opslag som "dagens" uden dato.
    await addDoc(collection(db, `room_notices/${roomId}/boards/${boardOwnerId}/messages`), {
      authorId: boardOwnerId, authorName: boardOwnerName,
      sections: activeDraftCats.reduce((acc, cat) => {
        if (draftSections[cat]?.trim()) acc[cat] = {
          text: draftSections[cat], link: draftLinks[cat] || null,
          visibility: draftSectionVisibility[cat] || 'all',
          allowedStudentIds: draftSectionVisibility[cat] === 'specific' ? (draftSectionStudents[cat] || []) : null
        };
        return acc;
      }, {} as Record<string, any>),
      timestamp: Date.now(),
    });
    await setDoc(doc(db, `room_notices/${roomId}/boards/${boardOwnerId}`), {
      guardianName: boardOwnerName, subjectName: boardTitle || subjectName || '', subjectTitle: boardTitle || subjectName || '', lastUpdated: Date.now(),
    }, { merge: true });
    setDraftSections({}); setDraftLinks({}); setDraftSectionVisibility({}); setDraftSectionStudents({});
    setActiveDraftCats(['general']);
  };

  const handleDelete = async (id: string) => {
    await deleteDoc(doc(db, `room_notices/${roomId}/boards/${boardOwnerId}/messages`, id));
  };

  const handleDeleteBoard = async () => {
    if (!confirm('Slet hele denne Vogterrulle permanent?')) return;
    await deleteDoc(doc(db, `room_notices/${roomId}/boards/${boardOwnerId}`));
    onClose();
  };

  const handleSaveEdit = async (id: string) => {
    await updateDoc(doc(db, `room_notices/${roomId}/boards/${boardOwnerId}/messages`, id), {
      sections: editingSections, updatedAt: Date.now(),
    });
    setEditingId(null);
  };

  const todayDate = new Date().toDateString();
  const todayMsgs = messages.filter(m => new Date(m.timestamp).toDateString() === todayDate);
  const archiveMsgs = messages.filter(m => new Date(m.timestamp).toDateString() !== todayDate);

  const getWeekLabel = (ts: number) => {
    const d = new Date(ts);
    const weekStart = new Date(d);
    weekStart.setDate(d.getDate() - d.getDay() + 1);
    return weekStart.toLocaleDateString('da-DK', { day: 'numeric', month: 'long' }) + ' ugen';
  };

  const filteredArchive = archiveMsgs.filter(m => {
    if (archiveSearchDate && new Date(m.timestamp).toLocaleDateString("da-DK") !== new Date(archiveSearchDate).toLocaleDateString("da-DK")) return false;
    if (archiveSearchGuardian && !boardOwnerName.toLowerCase().includes(archiveSearchGuardian.toLowerCase())) return false;
    return true;
  });
  const filteredArchiveByWeek: Record<string, any[]> = {};
  filteredArchive.forEach((m: any) => {
    const week = getWeekLabel(m.timestamp);
    filteredArchiveByWeek[week] = filteredArchiveByWeek[week] || [];
    filteredArchiveByWeek[week].push(m);
  });
  const archiveByWeek: Record<string, any[]> = {};
  archiveMsgs.forEach(m => {
    const week = getWeekLabel(m.timestamp);
    archiveByWeek[week] = archiveByWeek[week] || [];
    archiveByWeek[week].push(m);
  });

  const renderMessage = (msg: any) => {
    const sections = msg.sections || {};
    const isEditing = editingId === msg.id;
    // 18-8-2026: samme regel som Halls' campusBoardsForStudent.
    // guardian_only manglede her - den blev vist for elever.
    const visibleSections = Object.entries(sections).filter(([catId, val]: [string, any]) => {
      if (isGuardian) return true;
      if (val.visibility === 'guardian_only') return false;
      if (val.visibility === 'plus' && (currentUser as any).campusPackage !== 'plus') return false;
      if (val.visibility === 'specific') return val.allowedStudentIds?.includes(currentUser.id) || false;
      return true;
    });
    if (visibleSections.length === 0 && !isGuardian) return null;

    return (
      <div key={msg.id} className="relative bg-white/5 border border-white/10 p-4 rounded-xl group mb-3">
        <div className="flex justify-between items-start mb-3">
          <span className="text-[11px] text-white/60 font-mono">
            {new Date(msg.timestamp).toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' })}
            {msg.updatedAt && <span className="ml-2 opacity-50">(redigeret)</span>}
          </span>
        </div>
        {isEditing ? (
          <div className="space-y-2">
            {Object.entries(editingSections).map(([catId, val]: [string, any]) => {
              const cat = BUILT_IN_CATS.find(c => c.id === catId);
              return (
                <div key={catId} className={`${cat?.bg} border ${cat?.color} rounded-lg p-3`}>
                  <p className="text-[10px] font-black uppercase text-white/75 mb-1">{cat?.label}</p>
                  <textarea value={val.text || ''} onChange={e => setEditingSections(prev => ({
                    ...prev, [catId]: { ...prev[catId], text: e.target.value }
                  }))} className="w-full bg-black/30 text-white text-sm p-2 rounded outline-none h-20 resize-none leading-relaxed" />
                </div>
              );
            })}
            <div className="flex gap-2">
              <button onClick={() => handleSaveEdit(msg.id)} className="bg-white/10 text-white px-4 py-1.5 rounded text-[10px] font-black uppercase hover:bg-white/20 border border-white/20">Gem</button>
              <button onClick={() => setEditingId(null)} className="text-white/70 text-[10px] font-black uppercase hover:text-white">Annuller</button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {/* 18-8-2026: visibleSections, IKKE sections. Foer blev alle sektioner
                renderet selvom filteret var beregnet - plus- og specific-indhold
                laekkede til elever der ikke maatte se det. */}
            {visibleSections.map(([catId, val]: [string, any]) => {
              const cat = BUILT_IN_CATS.find(c => c.id === catId);
              return (
                <div key={catId} className={`${cat?.bg || 'bg-white/5'} border ${cat?.color || 'border-white/10'} rounded-lg p-3`}>
                  <p className="text-[10px] font-black uppercase tracking-widest text-white/70 mb-2">{cat?.label || catId}</p>
                  <p className="text-white text-sm leading-relaxed whitespace-pre-wrap">{val.text}</p>
                  {val.link && (
                    <a href={val.link} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[10px] text-green-400 hover:text-green-300 mt-2 bg-green-900/20 border border-green-800/30 px-2 py-1 rounded-lg transition-all hover:bg-green-900/40">
                      🔗 {catId === 'homework' ? 'Se opgaven' : catId === 'summary' ? 'Se materiale' : 'Åbn link'}
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {(isOwner || isGuardian) && !isEditing && (
          <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 flex gap-2 transition-all">
            {canManage && (
              <button onClick={() => { setEditingId(msg.id); setEditingSections({ ...msg.sections }); }}
                className="text-white/70 hover:text-white text-[10px] font-black uppercase">Ret</button>
            )}
            <button onClick={() => handleDelete(msg.id)} className="text-red-800 hover:text-red-500 text-[10px] font-black uppercase">Slet</button>
          </div>
        )}
      </div>
    );
  };

  const tabs = isGuardian
    ? [
        { id: 'board',    label: '📋 Rulle' },
        { id: 'journals', label: `📔 Dagbøger${journalStats.total > 0 ? ` (${journalStats.hasEntry}/${journalStats.total})` : ''}` },
        { id: 'archive',  label: '🗄 Arkiv' },
      ]
    : [
        { id: 'board',   label: '📋 Rulle' },
        { id: 'archive', label: '🗄 Arkiv' },
      ];

  const FILTER_OPTIONS: { id: JournalFilter; label: string }[] = [
    { id: 'all',         label: `Alle (${journalStats.total})` },
    { id: 'today',       label: `Skrevet i dag (${journalStats.hasEntry})` },
    { id: 'missing',     label: `Mangler entry (${journalStats.total - journalStats.hasEntry})` },
    { id: 'low_mood',    label: `Lavt humør 😔 (${journalStats.lowMood})` },
    { id: 'no_feedback', label: `Afventer feedback (${journalStats.noFeedback})` },
  ];

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="w-full max-w-2xl bg-[#0f0d1a] border border-indigo-400/30 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">

        {/* HEADER */}
        <div className="p-5 border-b border-white/10 bg-white/5 flex justify-between items-center shrink-0">
          <div>
            {canManage ? (
              editingTitle ? (
                <div className="flex items-center gap-2">
                  <input value={boardTitle} onChange={e => setBoardTitle(e.target.value)}
                    placeholder="Fx Tallenes Labyrint · Drakes 4-6"
                    className="bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-sm outline-none focus:border-white/40 w-56" />
                  <button onClick={saveBoardTitle} className="text-[11px] text-white font-black uppercase hover:text-green-400">Gem</button>
                  <button onClick={() => setEditingTitle(false)} className="text-[11px] text-white/60 font-black uppercase">✕</button>
                </div>
              ) : (
                <h2 onClick={() => setEditingTitle(true)} className="text-xl font-cinzel text-white tracking-widest uppercase cursor-pointer hover:text-white/70 transition-all">
                  {boardTitle || '✏️ Tilføj fagnavn'}
                </h2>
              )
            ) : (
              <h2 className="text-xl font-cinzel text-white tracking-widest uppercase">{boardTitle || subjectName || 'Vogterens Rulle'}</h2>
            )}
            <p className="text-[11px] text-white/60 uppercase tracking-widest mt-1">{boardOwnerName}</p>
            {(isOwner || isGuardian) && (
              <div className="mt-2">
                {linkedSubjectId ? (
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] text-blue-400/70 font-black uppercase">
                      🚪 {hallsData.find(h => h.id === linkedHallId)?.subjects?.find((s: any) => s.id === linkedSubjectId)?.name?.da || 'Fagdør tilknyttet'}
                    </span>
                    {subjectResourceLinks.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {subjectResourceLinks.map((link, i) => (
                          <a key={i} href={link.url} target="_blank" rel="noreferrer"
                            className="flex items-center gap-1 text-[8px] font-black uppercase px-2 py-1 bg-green-900/20 border border-green-500/20 text-green-300 rounded-lg hover:bg-green-900/40 transition">
                            🔗 {link.title}
                          </a>
                        ))}
                      </div>
                    )}
                    <span style={{display:'none'}}>
                    </span>
                    <button onClick={async () => {
                      setLinkedSubjectId(''); setLinkedHallId('');
                      const { setDoc: sd2, doc: d2 } = await import('firebase/firestore');
                      await sd2(d2(db, `room_notices/${roomId}/boards/${boardOwnerId}`), { linkedSubjectId: null, linkedHallId: null }, { merge: true });
                    }} className="text-[9px] text-white/30 hover:text-red-400">✕</button>
                  </div>
                ) : (
                  <details className="group">
                    <summary className="text-[9px] text-blue-400/50 hover:text-blue-300 cursor-pointer font-black uppercase list-none">+ Kobl til AcademyHalls fagdør</summary>
                    <div className="mt-2 space-y-1 max-h-40 overflow-y-auto no-scrollbar">
                      {hallsData.map((hall: any) => (
                        <div key={hall.id}>
                          <p className="text-[8px] text-white/30 font-black uppercase px-1 mt-1">{hall.name}</p>
                          {(hall.subjects || []).filter((s: any) => s.isActive !== false).map((subj: any) => {
                            const subjName = typeof subj.name === 'string' ? subj.name : subj.name?.da || subj.name?.en || 'Fagdør';
                            return (
                              <button key={subj.id} onClick={async () => {
                                setLinkedSubjectId(subj.id); setLinkedHallId(hall.id);
                                const { setDoc: sd2, doc: d2 } = await import('firebase/firestore');
                                await sd2(d2(db, `room_notices/${roomId}/boards/${boardOwnerId}`), { linkedSubjectId: subj.id, linkedHallId: hall.id }, { merge: true });
                              }} className="w-full text-left text-[9px] px-2 py-1 rounded bg-blue-900/20 hover:bg-blue-900/40 text-blue-300 border border-blue-500/20 transition-all mb-1">
                                {subjName}
                              </button>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            )}
            {canManage && colleagues && colleagues.length > 1 && (
              <div className="mt-2">
                {showAssign ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-white/60 uppercase">Tildel til:</span>
                    {colleagues.filter(o => o.id !== currentUser.id).map(g => (
                      <button key={g.id} onClick={async () => {
                        await setDoc(doc(db, `room_notices/${roomId}/boards/${g.id}`), {
                          guardianId: g.id, guardianName: g.wizardName || g.name,
                          subjectName: boardTitle || subjectName || '', subjectTitle: boardTitle || subjectName || '', isVisible: true, lastUpdated: Date.now(),
                        }, { merge: true });
                        setShowAssign(false);
                      }} className="text-xs px-3 py-1.5 bg-white/10 text-white rounded-lg border border-white/20 hover:bg-white/20 transition-all">
                        {g.wizardName || g.name}
                      </button>
                    ))}
                    <button onClick={() => setShowAssign(false)} className="text-xs text-white/60 hover:text-white">✕</button>
                  </div>
                ) : (
                  <button onClick={() => setShowAssign(true)} className="text-xs text-white/60 hover:text-white/60 uppercase font-black transition-all">
                    + Tildel til kollega
                  </button>
                )}
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white text-2xl transition-all">&times;</button>
        </div>

        {/* TABS */}
        <div className="flex border-b border-white/10 shrink-0">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id as any)}
              className={`flex-1 py-2.5 text-[10px] font-black uppercase tracking-widest transition-all ${
                activeTab === tab.id
                  ? 'text-yellow-500 border-b-2 border-yellow-500 bg-yellow-500/5'
                  : 'text-white/60 hover:text-white/60'
              }`}>
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4 no-scrollbar">

          {/* ── RULLE ──────────────────────────────────────────── */}
          {activeTab === 'board' && (
            <>
              {/* DAGENS FOKUS + DAGLIG BESKED FRA HALLS */}
              {(pinnedActivity || subjectDailyMsg) && (
                <div className="space-y-3 mb-2">
                  {(subjectDailyMsg !== undefined || linkedSubjectId) && (canManage || !_beskedGammel(subjectDailyMsg, subjectDailyAt)) && (
                    <div className="bg-purple-950/30 border border-purple-500/30 rounded-xl p-4 space-y-2">
                      <div className="flex justify-between items-center">
                        <p className="text-[9px] font-black uppercase text-purple-400/70 tracking-widest">📣 Dagens besked{subjectDailyAt ? <span className="ml-2 text-white/40 font-bold normal-case tracking-normal">{'\u00b7'} {new Date(subjectDailyAt).toLocaleDateString('da-DK', { day: 'numeric', month: 'short' })}</span> : null}{canManage && _beskedGammel(subjectDailyMsg, subjectDailyAt) ? <span className="ml-2 text-red-300 font-bold normal-case tracking-normal">{'\u00b7'} {subjectDailyAt ? 'vises ikke for eleverne (ældre end 14 dage), skriv en ny' : 'uden dato, vises ikke for eleverne, gem den igen eller skriv en ny'}</span> : null}</p>
                        {isOwner && (
                          <span className="text-[8px] text-purple-400/50 italic">Synlig i AcademyHalls fagdør + Vogterrulle</span>
                        )}
                      </div>
                      {canManage ? (
                        <div className="space-y-2">
                          <textarea
                            value={subjectDailyMsg}
                            onChange={e => setSubjectDailyMsg(e.target.value)}
                            placeholder="Skriv dagens besked til eleverne..."
                            rows={2}
                            className="w-full bg-black/30 border border-purple-500/20 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-purple-400 resize-none"
                          />
                          <button onClick={async () => {
                            if (!linkedSubjectId || !linkedHallId) return;
                            // Phase 3: skriv direkte til flat subjects collection
                            const { doc: d2, updateDoc, setDoc: sd2 } = await import('firebase/firestore');
                            const naa = Date.now();
                            // 18-8-2026: dailyMessageAt saa eleven kan se HVORNAAR beskeden
                            // sidst blev rettet - feltet overskrives jo, saa uden dato ved hun
                            // ikke om den er fra i dag eller i forgaars.
                            await updateDoc(d2(db, 'subjects', linkedSubjectId), { dailyMessage: { da: subjectDailyMsg, en: subjectDailyMsg }, dailyMessageAt: naa });
                            // lastUpdated paa tavlen -> eleven faar den gule prik igen.
                            // Uden dette gik en opdateret dagsbesked HELT ubemaerket hen.
                            try { await sd2(d2(db, `room_notices/${roomId}/boards`, boardOwnerId), { lastUpdated: naa }, { merge: true }); }
                            catch (e) { console.warn('lastUpdated fejlede:', e); }
                            setSubjectDailyAt(naa);
                          }} className="bg-purple-700 hover:bg-purple-600 text-white px-4 py-1.5 rounded-lg text-[9px] font-black uppercase transition">
                            📣 Gem besked
                          </button>
                        </div>
                      ) : (
                        _beskedGammel(subjectDailyMsg, subjectDailyAt) ? null : <p className="text-sm text-white/80 leading-relaxed italic">"{subjectDailyMsg}"</p>
                      )}
                    </div>
                  )}
                  {((pinnedActivity && (canManage || !_fokusUdloebet(pinnedActivity))) || (canManage && subjectActivities.length > 0)) && (
                    <div className="bg-yellow-950/30 border border-yellow-500/30 rounded-xl p-4 space-y-3">
                      <div className="flex justify-between items-center">
                        <p className="text-[9px] font-black uppercase text-yellow-400/70 tracking-widest">⭐ Dagens Fokus</p>
                        <div className="flex items-center gap-2">
                          {isOwner && <span className="text-[8px] text-yellow-400/40 italic">Slår igennem i AcademyHalls</span>}
                          {pinnedActivity && (
                            <button onClick={() => openPinnedActivity(pinnedActivity)}
                              className="text-[9px] bg-yellow-700/40 hover:bg-yellow-600/60 border border-yellow-500/40 text-yellow-300 px-3 py-1 rounded-lg font-black uppercase transition-all">
                              Åbn →
                            </button>
                          )}
                        </div>
                      </div>
                      {canManage && subjectActivities.length > 0 && (
                        <select
                          value={pinnedActivityId}
                          onChange={async e => {
                            const newId = e.target.value;
                            setPinnedActivityId(newId);
                            const act = subjectActivities.find((a: any) => a.id === newId) || null;
                            setPinnedActivity(act);
                            if (!linkedSubjectId || !linkedHallId) return;
                            // 30-8-2026: pin'en har TO hjem, og vi kendte kun det ene.
                            // Halls' updateSubject skriver BAADE 'subjects' og
                            // 'subject_placements/{subjectId}__{classroomId}', og
                            // campusBoardsForStudent laeser PLACEMENTEN foerst
                            // ("den hal-specifikke sandhed vinder"). Skrev vi kun det flade
                            // dokument, ville et pin sat i Halls blive staaende paa
                            // Borgpladsen, mens Campus viste noget andet.
                            // hallId ER classroomId - samme noegle funktionen slaar op med.
                            const { doc: d2, updateDoc, setDoc: sdPl } = await import('firebase/firestore');
                            await updateDoc(d2(db, 'subjects', linkedSubjectId), { pinnedActivityId: newId });
                            const _plId = linkedSubjectId + '__' + linkedHallId;
                            // merge: placementen baerer ogsaa notice, portalfelter, visibility
                            // og order — vi maa roere ET felt og intet andet.
                            await sdPl(d2(db, 'subject_placements', _plId), {
                              id: _plId,
                              subjectId: linkedSubjectId,
                              classroomId: linkedHallId,
                              pinnedActivityId: newId,
                            }, { merge: true });
                          }}
                          className="w-full bg-black/30 border border-yellow-500/20 rounded-lg px-3 py-2 text-white text-xs outline-none focus:border-yellow-400"
                        >
                          <option value="">— Ingen pinned opgave —</option>
                          {subjectActivities.map((a: any) => (
                            <option key={a.id} value={a.id}>
                              {typeof a.title === 'string' ? a.title : a.title?.da || a.title?.en || ''} ({a.topicTitle})
                            </option>
                          ))}
                        </select>
                      )}
                      {pinnedActivity && (canManage || !_fokusUdloebet(pinnedActivity)) && (<div className="flex gap-3 items-start">
                        {pinnedActivity.contentBlocks?.find((b: any) => b.type === 'image') && (
                          <img
                            src={pinnedActivity.contentBlocks.find((b: any) => b.type === 'image').content}
                            className="w-14 h-14 rounded-xl object-cover border border-yellow-500/30 shrink-0"
                          />
                        )}
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-white leading-tight">
                            {typeof pinnedActivity.title === 'string' ? pinnedActivity.title : pinnedActivity.title?.da || pinnedActivity.title?.en || ''}
                          </p>
                          <p className="text-[9px] text-yellow-400/60 uppercase mt-1">{pinnedActivity.type} · {pinnedActivity.xpReward} XP</p>
                          {pinnedActivity.deadline && (
                            <p className={`text-[9px] mt-1 font-mono ${Date.now() > pinnedActivity.deadline ? 'text-red-400' : 'text-yellow-400/70'}`}>
                              ⌛ {new Date(pinnedActivity.deadline).toLocaleDateString('da-DK', {day:'numeric', month:'short'})}{canManage && _fokusUdloebet(pinnedActivity) ? ' \u00b7 frist passeret, vises ikke længere for eleverne' : ''}
                            </p>
                          )}
                        </div>
                      </div>
                      )}
                      {isOwner && (
                        <div className="pt-2 border-t border-yellow-500/10">
                          <p className="text-[8px] text-white/25 italic">Sæt Dagens Fokus i Kancelliet → fagdør → Indhold-fanen</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {canManage && (
                <div className="bg-white/5 p-4 rounded-xl border border-white/10 space-y-3">
                  <div className="flex justify-between items-center">
                    <p className="text-[10px] font-black uppercase text-white/70 tracking-widest">Nyt opslag</p>
                    {isOwner && (<button onClick={handleDeleteBoard} className="text-xs text-red-400 hover:text-red-300 font-black uppercase transition-all">🗑 Slet rulle</button>)}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {BUILT_IN_CATS.map(cat => (
                      <button key={cat.id} onClick={() => toggleDraftCat(cat.id)}
                        className={`text-xs px-3 py-1.5 rounded-full font-black uppercase border transition-all ${activeDraftCats.includes(cat.id) ? 'bg-white/20 text-white border-white/40' : 'border-white/20 text-white/60 hover:bg-white/10'}`}>
                        {cat.label}
                      </button>
                    ))}
                  </div>
                  {activeDraftCats.map(catId => {
                    const cat = BUILT_IN_CATS.find(c => c.id === catId);
                    const catVis = draftSectionVisibility[catId] || 'all';
                    return (
                      <div key={catId} className={`${cat?.bg} border ${cat?.color} rounded-xl p-3 space-y-2`}>
                        <div className="flex justify-between items-center">
                          <p className="text-[10px] font-black uppercase text-white/70 tracking-widest">{cat?.label}</p>
                          <select value={catVis}
                            onChange={e => {
                              setDraftSectionVisibility(prev => ({ ...prev, [catId]: e.target.value }));
                              if (e.target.value !== 'specific') setDraftSectionStudents(prev => ({ ...prev, [catId]: [] }));
                            }}
                            className="bg-black/40 border border-white/20 text-white text-xs rounded-lg px-3 py-1.5 outline-none">
                            <option value="all">👁 Alle</option>
                            <option value="standard">📦 Standard</option>
                            <option value="plus">⭐ Plus</option>
                            {students.length > 0 && <option value="specific">👤 Vælg elev(er)</option>}
                          </select>
                        </div>
                        {catVis === 'specific' && students.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {students.map(st => {
                              const selected = (draftSectionStudents[catId] || []).includes(st.id);
                              return (
                                <button key={st.id} onClick={() => setDraftSectionStudents(prev => {
                                  const cur = prev[catId] || [];
                                  return { ...prev, [catId]: selected ? cur.filter(id => id !== st.id) : [...cur, st.id] };
                                })} className={`text-xs px-3 py-1.5 rounded-full border-2 transition-all font-bold ${selected ? 'bg-indigo-600 text-white border-indigo-400' : 'border-white/20 text-white hover:bg-white/10'}`}>
                                  {s(st.wizardName) || st.name}
                                </button>
                              );
                            })}
                          </div>
                        )}
                        <textarea value={draftSections[catId] || ''}
                          onChange={e => setDraftSections(prev => ({ ...prev, [catId]: e.target.value }))}
                          placeholder="Skriv her..."
                          className="w-full bg-black/30 border border-white/10 p-3 rounded-lg text-white text-sm leading-relaxed outline-none focus:border-white/30 h-20 resize-none" />
                        {(catId === 'link' || (cat as any)?.hasUrl) && (
                          <div className="space-y-1.5 mt-1">
                            <p className="text-[9px] font-black uppercase tracking-widest text-white/40">Vælg fra akademiet</p>
                            <div className="flex gap-2">
                              <select value={draftSrc[catId] || ''}
                                onChange={e => { setDraftSrc(prev => ({ ...prev, [catId]: e.target.value })); setDraftLinks(prev => ({ ...prev, [catId]: '' })); }}
                                className="flex-1 min-w-0 bg-black/30 border border-white/10 rounded-lg px-2 py-1.5 text-[11px] text-white outline-none focus:border-green-600/50">
                                <option value="">— Ingen kilde —</option>
                                {subjectActivities.length > 0 ? (
                                  <option value="_subject_activities">{'\u{1F4DC}'} Dette fags opgaver</option>
                                ) : null}
                                {srcCatalog.map((k: any) => (<option key={k.key} value={k.key}>{k.label}</option>))}
                              </select>
                              {draftSrc[catId] ? (
                                <select value={''}
                                  onChange={e => setDraftLinks(prev => ({ ...prev, [catId]: byggKildeLink(draftSrc[catId], e.target.value) }))}
                                  className="flex-1 min-w-0 bg-black/30 border border-white/10 rounded-lg px-2 py-1.5 text-[11px] text-white outline-none focus:border-green-600/50">
                                  <option value="">— Vælg indhold —</option>
                                  {(draftSrc[catId] === '_subject_activities'
                                    ? subjectActivities.map((a: any) => ({
                                        id: a.id,
                                        name: (s(a.title) || a.id) + (a.topicTitle ? ' (' + s(a.topicTitle) + ')' : ''),
                                      }))
                                    : ((srcCatalog.find((k: any) => k.key === draftSrc[catId]) || {}).items || [])
                                  ).map((it: any) => (
                                    <option key={it.id} value={it.id}>{it.name}</option>
                                  ))}
                                </select>
                              ) : null}
                            </div>
                            <p className="text-[9px] text-white/30 italic">eller</p>
                            <input value={draftLinks[catId] || ''}
                              onChange={e => setDraftLinks(prev => ({ ...prev, [catId]: e.target.value }))}
                              placeholder={catId === 'link' ? '🔗 Egen URL' : '🔗 Egen URL (valgfrit)'}
                              className="w-full bg-black/30 border border-white/10 p-2 rounded-lg text-white text-xs outline-none focus:border-green-600/50" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div className="flex justify-end pt-1">
                    <button onClick={handlePost}
                      className="bg-white/10 hover:bg-white/20 text-white px-6 py-2 rounded-xl font-black uppercase text-[10px] tracking-widest border border-white/20 transition-all">
                      Post ✨
                    </button>
                  </div>
                </div>
              )}

              {isCurrentUserStudent && (
                <div className="bg-purple-950/20 p-4 rounded-xl border border-purple-800/20 space-y-2">
                  <p className="text-[10px] font-black uppercase text-purple-400/70 tracking-widest">✅ Din check-ud</p>
                  <textarea value={draftSections['checkin'] || ''}
                    onChange={e => setDraftSections(prev => ({ ...prev, checkin: e.target.value }))}
                    placeholder="Hvad lærte du? Hvad var svært?"
                    className="w-full bg-white/5 border border-purple-800/20 p-3 rounded-xl text-white text-sm outline-none focus:border-purple-600/50 h-16 resize-none leading-relaxed" />
                  <button onClick={async () => {
                    if (!draftSections['checkin']?.trim()) return;
                    await addDoc(collection(db, `room_notices/${roomId}/boards/${boardOwnerId}/messages`), {
                      authorId: currentUser.id, authorName: currentUser.wizardName || currentUser.name,
                      sections: { checkin: { text: draftSections['checkin'] } },
                      visibility: 'guardian_only', timestamp: Date.now(),
                    });
                    setDraftSections(prev => ({ ...prev, checkin: '' }));
                  }} className="bg-purple-900/40 text-purple-300 px-5 py-2 rounded-xl font-black uppercase text-[10px] hover:bg-purple-800/40 border border-purple-800/30 transition-all">
                    Send ✅
                  </button>
                </div>
              )}

              {todayMsgs.length > 0
                ? <div><p className="text-xs text-white/20 uppercase font-black tracking-widest mb-3">I dag</p>{todayMsgs.map(renderMessage)}</div>
                : <div className="text-center py-12"><p className="font-cinzel italic text-white/75 text-sm">Ingen opslag i dag endnu...</p></div>
              }
            </>
          )}

          {/* ── DAGBØGER ───────────────────────────────────────── */}
          {activeTab === 'journals' && isGuardian && (
            <div className="space-y-3">

              {/* STATISTIK */}
              <div className="grid grid-cols-4 gap-2">
                {[
                  { label: 'Skrevet',  value: journalStats.hasEntry,                              color: 'text-green-400' },
                  { label: 'Mangler',  value: journalStats.total - journalStats.hasEntry,          color: 'text-red-400' },
                  { label: 'Lavt 😔',  value: journalStats.lowMood,                               color: 'text-orange-400' },
                  { label: 'Afventer', value: journalStats.noFeedback,                             color: 'text-yellow-400' },
                ].map(stat => (
                  <div key={stat.label} className="bg-black/30 border border-white/5 rounded-xl p-2 text-center">
                    <p className={`text-lg font-black ${stat.color}`}>{stat.value}</p>
                    <p className="text-xs text-white/60 uppercase font-black">{stat.label}</p>
                  </div>
                ))}
              </div>

              {/* FILTER + SORTERING */}
              <div className="flex gap-2">
                <select
                  value={journalFilter}
                  onChange={e => { setJournalFilter(e.target.value as JournalFilter); setOpenStudentId(null); }}
                  className="flex-1 bg-black/40 border border-white/10 text-white text-[9px] font-black rounded-lg px-2.5 py-2 outline-none"
                >
                  {FILTER_OPTIONS.map(f => (
                    <option key={f.id} value={f.id}>{f.label}</option>
                  ))}
                </select>
                <button
                  onClick={() => setJournalSort(prev => prev === 'name' ? 'mood_asc' : 'name')}
                  className={`px-3 py-2 rounded-lg border text-[10px] font-black uppercase transition-all whitespace-nowrap ${
                    journalSort === 'mood_asc'
                      ? 'bg-orange-600/30 border-orange-500/50 text-orange-300'
                      : 'bg-black/30 border-white/10 text-white/70 hover:text-white/70'
                  }`}
                >
                  {journalSort === 'mood_asc' ? '😔 Humør ▲' : 'A–Z'}
                </button>
              </div>

              {/* LEGENDE */}
              <div className="flex gap-3 text-xs text-white/25 font-black uppercase">
                <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-500/60 inline-block" /> Feedback givet</span>
                <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-yellow-400/60 inline-block" /> Afventer</span>
                <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-orange-500 inline-block" /> Lavt humør</span>
                <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-red-500/60 inline-block" /> Ingen entry</span>
              </div>

              {loadingEntries && <p className="text-white/60 text-[10px] italic text-center py-4">Henter dagbøger...</p>}

              {!loadingEntries && filteredStudents.length === 0 && (
                <div className="text-center py-8">
                  <p className="font-cinzel italic text-white/60 text-sm">
                    {students.length === 0
                      ? ((classroomIds && classroomIds.length > 0) || classroomId)
                          ? 'Ingen elever i de tilknyttede classrooms'
                          : 'Ingen classrooms tilknyttet'
                      : 'Ingen elever matcher filteret'}
                  </p>
                </div>
              )}

              {!loadingEntries && filteredStudents.map(student => (
                <StudentJournalCard
                  key={student.id}
                  student={student}
                  isOpen={openStudentId === student.id}
                  onToggle={() => setOpenStudentId(prev => prev === student.id ? null : student.id)}
                />
              ))}
            </div>
          )}

          {/* ── ARKIV ──────────────────────────────────────────── */}
          {activeTab === 'archive' && (
            <div className="space-y-4">
              {/* Søgning */}
              <div className="grid grid-cols-2 gap-2">
                <input type="date" value={archiveSearchDate} onChange={e => setArchiveSearchDate(e.target.value)}
                  className="bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-white text-sm outline-none focus:border-indigo-400" />
                <input value={archiveSearchGuardian} onChange={e => setArchiveSearchGuardian(e.target.value)}
                  placeholder="Søg på vogter..."
                  className="bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-white text-sm outline-none focus:border-indigo-400" />
              </div>
              {filteredArchive.length === 0
                ? <div className="text-center py-12 opacity-20"><p className="font-cinzel italic text-white text-sm">Intet arkiv endnu.</p></div>
                : Object.entries(filteredArchiveByWeek).map(([week, posts]) => (
                  <div key={week}>
                    <button onClick={() => setArchiveWeek(archiveWeek === week ? null : week)}
                      className="w-full text-left text-xs text-white/70 uppercase font-black tracking-widest mb-2 border-b border-white/10 pb-1 flex justify-between items-center hover:text-white transition-all">
                      {week}
                      <span>{archiveWeek === week ? '▲' : '▼'}</span>
                    </button>
                    {archiveWeek === week && (posts as any[]).map(renderMessage)}
                  </div>
                ))
              }
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuardianBoard;